import React, { useState, useEffect } from 'react';
import { usePerformance } from '../contexts/PerformanceContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

const ReviewCycleManager = () => {
  const { 
    reviewCycles, 
    loading, 
    fetchReviewCycles, 
    createReviewCycle, 
    updateCycleStatus 
  } = usePerformance();
  const { hasAnyRole } = useAuth();
  
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    cycle_type: 'quarterly',
    review_type: 'annual',
    start_date: '',
    end_date: '',
    self_assessment_deadline: '',
    manager_review_deadline: '',
    description: '',
    include_peer_feedback: false,
    peer_feedback_deadline: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchReviewCycles();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Convert dates to ISO format
    const submitData = {
      ...formData,
      start_date: new Date(formData.start_date).toISOString(),
      end_date: new Date(formData.end_date).toISOString(),
      self_assessment_deadline: new Date(formData.self_assessment_deadline).toISOString(),
      manager_review_deadline: new Date(formData.manager_review_deadline).toISOString(),
      peer_feedback_deadline: formData.include_peer_feedback && formData.peer_feedback_deadline 
        ? new Date(formData.peer_feedback_deadline).toISOString() 
        : null
    };

    const result = await createReviewCycle(submitData);
    
    if (result.success) {
      setShowForm(false);
      setFormData({
        name: '',
        cycle_type: 'quarterly',
        review_type: 'annual',
        start_date: '',
        end_date: '',
        self_assessment_deadline: '',
        manager_review_deadline: '',
        description: '',
        include_peer_feedback: false,
        peer_feedback_deadline: ''
      });
    } else {
      setError(result.error);
    }
    
    setSubmitting(false);
  };

  const handleStatusChange = async (cycleId, newStatus) => {
    if (window.confirm(`Are you sure you want to change the cycle status to ${newStatus}?`)) {
      await updateCycleStatus(cycleId, newStatus);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      planning: 'bg-gray-100 text-gray-800',
      active: 'bg-green-100 text-green-800',
      closed: 'bg-blue-100 text-blue-800',
      archived: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Check if user can manage review cycles
  if (!hasAnyRole(['hr', 'admin'])) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
            <p className="text-gray-600 mb-6">
              You don't have permission to manage review cycles. This feature is available for HR and Admin users only.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Review Cycle Management</h1>
            <p className="text-gray-600 mt-2">Create and manage performance review cycles</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Create Review Cycle
          </button>
        </div>

        {/* Create Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Create Review Cycle</h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Cycle Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="e.g., Q1 2024 Performance Review"
                      />
                    </div>

                    <div>
                      <label htmlFor="cycle_type" className="block text-sm font-medium text-gray-700">
                        Cycle Type *
                      </label>
                      <select
                        id="cycle_type"
                        name="cycle_type"
                        required
                        value={formData.cycle_type}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        <option value="quarterly">Quarterly</option>
                        <option value="bi_annual">Bi-Annual</option>
                        <option value="annual">Annual</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="review_type" className="block text-sm font-medium text-gray-700">
                      Review Type *
                    </label>
                    <select
                      id="review_type"
                      name="review_type"
                      required
                      value={formData.review_type}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="annual">Annual</option>
                      <option value="mid_year">Mid-Year</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="probation">Probation</option>
                      <option value="promotion">Promotion</option>
                    </select>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
                        Start Date *
                      </label>
                      <input
                        type="date"
                        id="start_date"
                        name="start_date"
                        required
                        value={formData.start_date}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
                        End Date *
                      </label>
                      <input
                        type="date"
                        id="end_date"
                        name="end_date"
                        required
                        value={formData.end_date}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Deadlines */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="self_assessment_deadline" className="block text-sm font-medium text-gray-700">
                        Self-Assessment Deadline *
                      </label>
                      <input
                        type="date"
                        id="self_assessment_deadline"
                        name="self_assessment_deadline"
                        required
                        value={formData.self_assessment_deadline}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="manager_review_deadline" className="block text-sm font-medium text-gray-700">
                        Manager Review Deadline *
                      </label>
                      <input
                        type="date"
                        id="manager_review_deadline"
                        name="manager_review_deadline"
                        required
                        value={formData.manager_review_deadline}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Peer Feedback */}
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="include_peer_feedback"
                        checked={formData.include_peer_feedback}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include 360-degree peer feedback</span>
                    </label>
                  </div>

                  {formData.include_peer_feedback && (
                    <div>
                      <label htmlFor="peer_feedback_deadline" className="block text-sm font-medium text-gray-700">
                        Peer Feedback Deadline
                      </label>
                      <input
                        type="date"
                        id="peer_feedback_deadline"
                        name="peer_feedback_deadline"
                        value={formData.peer_feedback_deadline}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                  )}

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Description of the review cycle..."
                    />
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <div className="flex">
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">
                            {error}
                          </h3>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Buttons */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
                    >
                      {submitting ? (
                        <LoadingSpinner size="sm" text="" />
                      ) : (
                        'Create Cycle'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Cycles List */}
        {loading ? (
          <LoadingSpinner size="lg" text="Loading review cycles..." />
        ) : reviewCycles.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No review cycles found</h3>
            <p className="text-gray-500">Create your first review cycle to get started!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reviewCycles.map(cycle => (
              <div key={cycle.id} className="bg-white shadow rounded-lg overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-medium text-gray-900">{cycle.name}</h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(cycle.status)}`}>
                          {cycle.status.replace('_', ' ').toUpperCase()}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {cycle.cycle_type.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      
                      {cycle.description && (
                        <p className="text-gray-600 mt-2">{cycle.description}</p>
                      )}
                      
                      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm text-gray-500">
                        <div>
                          <span className="font-medium">Period:</span> {formatDate(cycle.start_date)} - {formatDate(cycle.end_date)}
                        </div>
                        <div>
                          <span className="font-medium">Self-Assessment:</span> {formatDate(cycle.self_assessment_deadline)}
                        </div>
                        <div>
                          <span className="font-medium">Manager Review:</span> {formatDate(cycle.manager_review_deadline)}
                        </div>
                        <div>
                          <span className="font-medium">Reviews:</span> {cycle.completed_reviews}/{cycle.total_reviews}
                        </div>
                      </div>
                    </div>
                    
                    {/* Status Actions */}
                    <div className="flex items-center space-x-2">
                      {cycle.status === 'planning' && (
                        <button
                          onClick={() => handleStatusChange(cycle.id, 'active')}
                          className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                          Activate
                        </button>
                      )}
                      {cycle.status === 'active' && (
                        <button
                          onClick={() => handleStatusChange(cycle.id, 'closed')}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Close
                        </button>
                      )}
                      {cycle.status === 'closed' && (
                        <button
                          onClick={() => handleStatusChange(cycle.id, 'archived')}
                          className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                        >
                          Archive
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewCycleManager;